// src/data/duiInsurance.tsx
import React, { ReactNode } from "react";

/* =========================
   Interfaces (ReactNode-based)
========================= */
export interface DUIKeyComponent {
  name: ReactNode;
  description: ReactNode;
}

export interface DUIPremiumIncreaseExample {
  state: ReactNode;
  increase: ReactNode; // e.g., "50–150%" or <strong>50–150%</strong>
}

export interface DUIRecordDurationExample {
  state: ReactNode;
  recordDuration: ReactNode;
}

export interface DUIImpacts {
  premiumIncreases: {
    description: ReactNode;
    examples: DUIPremiumIncreaseExample[];
  };
  duration: {
    description: ReactNode;
    examples: DUIRecordDurationExample[];
  };
}

export interface DUIBenefit {
  title: ReactNode;
  description: ReactNode;
}

export interface DUISummaryRow {
  feature: ReactNode;
  description: ReactNode;
}

export interface DUIInsuranceInfoType {
  id: ReactNode;
  title: ReactNode;
  description: ReactNode;
  keyComponents: DUIKeyComponent[];
  impacts: DUIImpacts;
  benefits: DUIBenefit[];
  summaryTable: DUISummaryRow[];
}

/* =========================
   Content Object
========================= */
export const DUIInsuranceInfo: DUIInsuranceInfoType = {
  id: "dui-insurance-v01",
  title: <>DUI Insurance</>,
  description: (
    <>
      DUI insurance is not a separate policy—it's the coverage and filings (
      <strong>SR-22</strong>, <strong>FR-44</strong>) required after a DUI to meet
      state rules, reinstate driving privileges, and access high-risk markets.
    </>
  ),

  keyComponents: [
    {
      name: <>SR-22 Certificate</>,
      description: (
        <>
          Certificate of financial responsibility filed by your insurer with the DMV to
          prove you carry state-mandated liability coverage after a DUI. Typically
          required for <strong>3–5 years</strong>.
        </>
      ),
    },
    {
      name: <>FR-44 Certificate</>,
      description: (
        <>
          Specific to <strong>Florida</strong> and <strong>Virginia</strong>; requires
          higher liability limits (e.g., <em>$100k/$300k BI</em>, <em>$50k PD</em>, plus{" "}
          <em>$10k PIP</em> in Florida) for about <strong>3 years</strong>.
        </>
      ),
    },
    {
      name: <>Policy Types</>,
      description: (
        <>
          <strong>Vehicle SR-22/FR-44</strong> if you own a car (liability + optional
          collision/comprehensive) or a <strong>Non-Owner SR-22</strong> if you don’t
          own a vehicle but must meet state requirements.
        </>
      ),
    },
  ],

  impacts: {
    premiumIncreases: {
      description: (
        <>
          DUI convictions often raise premiums by <strong>50%–200%+</strong>, varying by
          state, insurer, and history. Some carriers may cancel or not renew.
        </>
      ),
      examples: [
        { state: <>California</>, increase: <>50–150%</> },
        { state: <>Ohio</>, increase: <>~111%</> },
        { state: <>National Avg.</>, increase: <>60–80%</> },
      ],
    },
    duration: {
      description: (
        <>
          A DUI can stay on your record for <strong>3–10 years</strong>. The steepest
          increases typically occur in the first <strong>3–5 years</strong>.
        </>
      ),
      examples: [
        { state: <>California</>, recordDuration: <>10 years</> },
        { state: <>General</>, recordDuration: <>3–10 years</> },
      ],
    },
  },

  benefits: [
    {
      title: <>Compliance with Legal Requirements</>,
      description: (
        <>SR-22/FR-44 filings are often mandatory to reinstate or retain a license.</>
      ),
    },
    {
      title: <>Restores Driving Privileges</>,
      description: <>Lets you drive legally again after meeting state mandates.</>,
    },
    {
      title: <>Access to High-Risk Markets</>,
      description: (
        <>Specialized insurers provide filings even if standard carriers won’t.</>
      ),
    },
    {
      title: <>Affordable Non-Owner Options</>,
      description: (
        <>Non-owner SR-22 can fulfill requirements if you don’t own a vehicle.</>
      ),
    },
    {
      title: <>Long-Term Financial Recovery</>,
      description: (
        <>
          Continuous coverage and a clean record over time can reduce premiums and help
          you transition back to standard markets.
        </>
      ),
    },
  ],

  summaryTable: [
    { feature: <>SR-22 / FR-44 Filing</>, description: <>Insurer files proof of required coverage.</> },
    { feature: <>Policy Types</>, description: <>Owner and non-owner liability options.</> },
    { feature: <>Premium Impact</>, description: <>Often +50–200% or more.</> },
    { feature: <>Duration</>, description: <>On record 3–10 years; biggest hit early.</> },
    { feature: <>Compliant Driving</>, description: <>Clean record helps lower rates.</> },
    { feature: <>Specialized Markets</>, description: <>High-risk carriers offer filings.</> },
  ],
};
